<script setup lang="ts">
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useRouter } from "vue-router";

import { ROUTES } from '@/constants/routes';
import { useAuthStore } from "@/modules/auth/store/authStore";
import missionsService from "@/modules/missions/services/missionsService";
import { type IMissionDB, MissionCommand } from '@/types/mission';

import type { IAgencyDB } from '@/types/agency';

import ComboButton from "@/dsComponents/buttons/ComboButton.vue";
import Dropdown from "@/dsComponents/Dropdown/Dropdown.vue";
import DropdownItem from "@/dsComponents/Dropdown/DropdownItem.vue";
import DropdownSeparator from "@/dsComponents/Dropdown/DropdownSeparator.vue";
import DropdownTitle from "@/dsComponents/Dropdown/DropdownTitle.vue";

import CompanyEmailMissing from "@/components/modal/CompanyEmailMissing.vue";
import TooMuchWorkerConfirm from "@/components/modal/TooMuchWorkerConfirm.vue";

// Define emits
// biome-ignore lint/suspicious/noExplicitAny: Init Biome
const emit = defineEmits<(e: "command", result: any) => void>();

// Props
interface Props {
    mission: IMissionDB;
    // biome-ignore lint/suspicious/noExplicitAny: Init Biome
    worker: any;
}

const props = defineProps<Props>();
const { t } = useI18n();

// State
const loading = ref(false);
const showTooMuchWorkerConfirm = ref(false);
const showCompanyEmailMissing = ref(false);
const previousCommand = ref("");
// biome-ignore lint/correctness/noUnusedVariables: Init Biome
const isOpen = ref(false);
// biome-ignore lint/suspicious/noExplicitAny: Init Biome
const popover = ref<any>(null);

// Store
const router = useRouter();
const authStore = useAuthStore();
// biome-ignore lint/correctness/noUnusedVariables: Init Biome
const isAgency = computed(() => authStore.isAgency);
// biome-ignore lint/correctness/noUnusedVariables: Init Biome
const currentAgency = computed<IAgencyDB | null>(() => authStore.currentAgency);

// Computed properties
const actionsCommands = computed(() => {
    return props.worker?.availableCommands?.filter((command: string) => command !== "workerReproposed") || [];
});

const informationsCommands = computed(() => {
    return props.worker?.availableCommands?.filter((command: string) => command === "workerReproposed") || [];
});

// Methods
// biome-ignore lint/suspicious/noExplicitAny: Init Biome
const sendCommand = async (command: string, data?: any) => {
    loading.value = true;
    try {
        if (!data) {
            data = {
                workerId: props.worker.workerId || props.worker.id,
            };
        } else {
            data = {
                ...data,
                workerId: props.worker.workerId || props.worker.id,
            };
        }

        const result = await missionsService.sendCommandMission({
            missionId: props.mission.id,
            command,
            data,
        });

        if (command === "workerStartContract") {
            showToast({
                message: t("mission.alerts.contractGenerated"),
                type: "success",
                duration: 10000,
            });
        }

        emit("command", result);
    // biome-ignore lint/suspicious/noExplicitAny: Init Biome
    } catch (e: any) {
        console.log(e);
        if (command === MissionCommand.workerPresent && e.errors && e.errors["email.missing"]) {
            showCompanyEmailMissing.value = true;
        }
        if (
            command === MissionCommand.workerConfirm &&
            e.errors &&
            e.fullErr?.response?.data &&
            e.fullErr?.response?.data[0]?.id === "position.full"
        ) {
            showTooMuchWorkerConfirm.value = true;
        }

        showToast({
            message: t("errors.Global.ServerError._default"),
            type: "error",
            duration: 10000,
        });
    }
    loading.value = false;
};

const displayWorkerReject = () => {
    showHideModal("WorkerReject", true);
};

const doCommand = (command: string) => {
    previousCommand.value = command;
    switch (command) {
        case MissionCommand.workerAgencyReject:
        case MissionCommand.workerCompanyReject:
            displayWorkerReject();
            break;
        default:
            sendCommand(command);
            break;
    }
    if (popover.value) {
        popover.value.hide();
    }
};

const label = (command: string) => {
    // biome-ignore lint/style/useTemplate: Init Biome
    return t("mission.availableCommands." + command + ".text");
};

const goToCompanyEdit = () => {
    showCompanyEmailMissing.value = false;
    router.push({
        name: ROUTES.APP.CLIENT._ROOT,
        params: { companyId: props.mission.client?.id || "" },
        query: { edit: "1" },
    });
};

const showToast = (options: { message: string; type: string; duration: number }) => {
    // Access the global toast object
    // biome-ignore lint/suspicious/noExplicitAny: Init Biome
    const vm: any = window as any;
    if (vm.$toast) {
        vm.$toast.open(options);
    }
};

// Helper to show or hide modals
const showHideModal = (modalName: string, show: boolean) => {
    showCompanyEmailMissing.value = modalName === "CompanyEmailMissing" && show;
    showTooMuchWorkerConfirm.value = modalName === "TooMuchWorkerConfirm" && show;
};
</script>

<template>
    <div class="WorkerAvailableCommands" v-if="actionsCommands.length">
        <Dropdown stop>
            <template #trigger-zone>
                <ComboButton icon-only="dots" variant="ghost" size="xs" />
            </template>
            <template #dropdown-content>
                <DropdownTitle label="Actions" />
                <DropdownItem v-for="(command, index) in actionsCommands" @click="doCommand(command)" :key="index">
                    {{ label(command) }}
                </DropdownItem>
                <template v-if="informationsCommands.length">
                    <DropdownSeparator />
                    <DropdownTitle label="Information" />
                    <DropdownItem v-for="(command, index) in informationsCommands" @click="doCommand(command)" :key="index">
                        {{ label(command) }}
                    </DropdownItem>
                </template>
            </template>
        </Dropdown>
        <CompanyEmailMissing
            v-if="showCompanyEmailMissing"
            :open="showCompanyEmailMissing"
            @close="showCompanyEmailMissing = false"
            @submit="goToCompanyEdit"
        />
        <TooMuchWorkerConfirm v-if="showTooMuchWorkerConfirm" :open="showTooMuchWorkerConfirm" @closed="showTooMuchWorkerConfirm = false" />
    </div>
</template>
<style lang="scss">
.workerTileAvailableCommands {
    top: -20px;
    padding: calc($padding / 2);
    cursor: pointer;

    .command {
        margin-bottom: calc($padding / 4);
        padding-bottom: calc($padding / 4);
        cursor: pointer;

        &:last-child {
            margin-bottom: 0;
            padding-bottom: 0;
        }
    }

    .informations {
        display: flex;
        border-bottom: 1px solid $lightgrey;
        padding-bottom: calc($padding / 4);
        margin-bottom: 5px;

        a {
            font-weight: bold;
        }
    }

    .actions {
        display: flex;
        border-bottom: 1px solid $lightgrey;
        padding-bottom: calc($padding / 4);
        margin-bottom: 5px;

        a {
            font-weight: bold;
        }
    }
}
.WorkerAvailableCommands {
    cursor: pointer;
    .new-icon {
        cursor: pointer;
        &:hover {
            opacity: 0.8;
        }

        &.design-system-dots-vertical {
            path {
                display: none;
            }
            svg {
                margin-top: 3px;
            }
        }
    }
}
</style>
